import { BookCard } from '@/components/book-card';
import { SearchFilter } from '@/components/search-filter';
import { SeoHead } from '@/components/seo-head';
import { Pagination } from '@/components/public/pagination';
import { useBookFilters } from '@/hooks/use-book-filters';
import { useTranslations } from '@/hooks/use-translations';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';
import PublicLayout from '@/layouts/public-layout';
import { Book, PaginatedData } from '@/types/models';
import { Head, Link } from '@inertiajs/react';

interface FamousBook {
    id: string;
    slug: string;
    title: string;
    author_id: string;
    author_name: string;
    cover_image: string;
}

interface FamousAuthor {
    id: string;
    name: string;
    slug: string;
    image?: string;
    books_count?: number;
}

interface Genre {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    long_description: string | null;
    meta_description: string | null;
    famous_books: string | Array<FamousBook> | null;
    famous_authors: string | Array<FamousAuthor> | null;
    similar_genres: Array<{ id: string; name: string; slug: string; translated_slug: string }> | null;
    books_count: number;
}

interface Author {
    id: string;
    name: string;
}

interface Props {
    genre: Genre;
    books: PaginatedData<Book>;
    authors: Author[];
    genres: Array<{ id: string; name: string }>;
    search: string;
    locale: string;
}

export default function GenrePage({ genre, books, authors, genres, search: initialSearch, locale }: Props) {
    const { t } = useTranslations();
    const { genreUrl, bookUrl, authorUrl } = useTranslatedRoutes();

    // Use the book filters hook
    const { filteredBooks, search, sort, authorId, genreId, handleSearch, handleSort, handleAuthorFilter, handleGenreFilter } = useBookFilters({
        books: books.data,
        initialSearch,
    });

    // Parse famous books if it's a JSON string
    const parseFamousBooks = (data: string | Array<FamousBook> | null): FamousBook[] => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    // Parse famous authors if it's a JSON string
    const parseFamousAuthors = (data: string | Array<FamousAuthor> | null): FamousAuthor[] => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const famousBooks = parseFamousBooks(genre.famous_books);
    const famousAuthors = parseFamousAuthors(genre.famous_authors);

    // Get the base URL from environment
    const baseUrl = import.meta.env.VITE_APP_URL || 'http://localhost:8000';
    
    // Get the full URL for this genre page
    const fullGenreUrl = baseUrl + genreUrl(genre.slug);
    
    // Generate structured data for genre collection
    const genreJsonLd = {
        "@context": "https://schema.org",
        "@type": "Collection",
        "name": genre.name,
        "description": genre.description || `Browse ${genre.name} books in our collection`,
        "numberOfItems": genre.books_count,
        "url": fullGenreUrl
    };

    return (
        <PublicLayout>
            <SeoHead
                title={genre.name}
                description={genre.meta_description || genre.description || `Discover ${genre.name} books. Browse our collection of ${genre.books_count} books in this genre.`}
                jsonLd={genreJsonLd}
            />

            {/* Main Content */}
            <section className="py-12">
                <div className="container mx-auto px-4">
                    {/* H1 - Genre Name and Intro */}
                    <div className="mb-8">
                        <h1 className="mb-4 text-3xl font-bold md:text-4xl">{genre.name}</h1>
                        {genre.long_description && (
                            <div className="prose prose-lg max-w-none">
                                <p className="text-muted-foreground whitespace-pre-line">{genre.long_description}</p>
                            </div>
                        )}
                    </div>

                    {/* H4 - Book List */}
                    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                        <h4 className="flex-shrink-0 text-xl font-semibold">{t(':genre Books', { genre: genre.name })}</h4>

                        {/* Search and Filter */}
                        <div className="flex-1">
                            <SearchFilter
                                onSearch={handleSearch}
                                onSort={handleSort}
                                onAuthorFilter={handleAuthorFilter}
                                onGenreFilter={handleGenreFilter}
                                authors={authors}
                                genres={[]} // Empty array to hide genre filter on genre pages
                                initialSearch={search}
                                initialSort={sort}
                                initialAuthorId={authorId}
                                initialGenreId={genreId}
                            />
                        </div>
                    </div>

                    {/* Books Grid */}
                    {filteredBooks.length === 0 ? (
                        <div className="py-12 text-center">
                            <p className="text-muted-foreground">
                                {search || authorId || genreId
                                    ? t('No :genre books match your filters', { genre: genre.name })
                                    : t('No books found in :genre genre', { genre: genre.name })}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                            {filteredBooks.map((book) => (
                                <BookCard key={book.id} book={book} translatedSlug={book.translated_slug} />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {books.links && books.links.length > 3 && (
                        <div className="mt-8 mb-12 flex justify-center">
                            <Pagination
                                links={books.links}
                                currentPage={books.current_page}
                                lastPage={books.last_page}
                            />
                        </div>
                    )}

                    {/* H2 - Famous Books Section */}
                    {famousBooks.length > 0 && (
                        <div className="mb-8">
                            <h2 className="mb-4 text-2xl font-semibold">
                                {t('Famous :genre Books', { genre: genre.name })}
                            </h2>
                            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                                {famousBooks.map((book) => (
                                    <Link
                                        key={book.id}
                                        href={bookUrl(book.slug)}
                                        className="group flex-shrink-0 w-28 block transition-transform hover:scale-105"
                                    >
                                        <div className="relative aspect-[2/3] bg-muted rounded-md overflow-hidden shadow-sm">
                                            <img
                                                src={book.cover_image}
                                                alt={book.title}
                                                className="h-full w-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="mt-2">
                                            <h3 className="text-xs font-medium line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                                                {book.title}
                                            </h3>
                                            <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">
                                                {book.author_name}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* H2 - Famous Authors Section */}
                    {famousAuthors.length > 0 && (
                        <div className="mb-8">
                            <h2 className="mb-4 text-2xl font-semibold">
                                {t('Famous :genre Authors', { genre: genre.name })}
                            </h2>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                {famousAuthors.map((author) => (
                                    <Link
                                        key={author.id}
                                        href={authorUrl(author.slug)}
                                        className="group flex items-center gap-4 rounded-lg border bg-card p-4 transition-all hover:shadow-md"
                                    >
                                        {author.image && (
                                            <div className="flex-shrink-0">
                                                <img
                                                    src={author.image}
                                                    alt={author.name}
                                                    className="h-16 w-16 rounded-full object-cover"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-medium group-hover:text-primary transition-colors truncate">
                                                {author.name}
                                            </h3>
                                            {author.books_count !== undefined && (
                                                <p className="text-sm text-muted-foreground">
                                                    {author.books_count} {author.books_count === 1 ? 'book' : 'books'}
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* H3 - Similar Genres Section */}
                    {genre.similar_genres && genre.similar_genres.length > 0 && (
                        <div className="mb-8">
                            <h3 className="mb-4 text-xl font-semibold">{t('Similar Genres')}</h3>
                            <div className="flex flex-wrap gap-2">
                                {genre.similar_genres.map((similarGenre) => (
                                    <a
                                        key={similarGenre.id}
                                        href={genreUrl(similarGenre.translated_slug || similarGenre.slug)}
                                        className="inline-flex items-center rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20"
                                    >
                                        {similarGenre.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
